import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import QRCode from 'qrcode.react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { uploadVideo, uploadMultipleVideos } from '../../utils/videoUploadManager';
import { utils as xlsxUtils, writeFile } from 'xlsx';

type MediaType = 'image' | 'video';
type MediaFile = File & { preview?: string, type: string };

const Container = styled.div`
  padding: 20px;
  animation: fadeIn 0.5s ease;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const Title = styled.h1`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.8rem;
  font-weight: 600;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const SearchContainer = styled.div`
  display: flex;
  margin-bottom: 25px;
  box-shadow: var(--box-shadow);
  border-radius: 8px;
  overflow: hidden;
`;

const SearchInput = styled.input`
  flex-grow: 1;
  padding: 14px 16px;
  border: none;
  font-size: 16px;
  transition: all 0.3s;
  background-color: var(--paper-color);
  color: var(--text-primary);
  font-family: 'Poppins', sans-serif;

  &:focus {
    outline: none;
    background-color: white;
  }
  
  [data-theme='dark'] & {
    background-color: #2d2d2d;
    
    &:focus {
      background-color: #333;
    }
  }
  
  &::placeholder {
    color: var(--text-light);
  }
`;

const SearchButton = styled.button`
  background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
  color: white;
  border: none;
  padding: 0 20px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: linear-gradient(to right, var(--primary-dark), var(--primary-color));
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const Table = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  animation: slideUp 0.5s ease;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px 20px;
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  [data-theme='dark'] & {
    background-color: #1a1a1a;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
  }
`;

const TableHeaderCell = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9rem;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  padding: 16px 20px;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: rgba(67, 97, 238, 0.03);
  }
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    
    &:hover {
      background-color: rgba(67, 97, 238, 0.1);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
  }
`;

const TableCell = styled.div`
  color: var(--text-primary);
  display: flex;
  align-items: center;
`;

const VisitorName = styled.div`
  font-weight: 500;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  padding: 6px 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  border-radius: 6px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 4px;
    width: 16px;
    height: 16px;
  }

  &:hover {
    background-color: rgba(67, 97, 238, 0.1);
  }

  &.delete {
    color: var(--error-color);
    
    &:hover {
      background-color: rgba(239, 71, 111, 0.1);
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
`;

const ModalContent = styled.div`
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  padding: 30px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.4s ease;
  
  [data-theme='dark'] & {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background-color: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-light);
  transition: color 0.2s;
  
  &:hover {
    color: var(--text-primary);
  }
`;

const QrCodeContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 25px;
`;

const QrCodeWrapper = styled.div`
  background-color: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  margin-bottom: 15px;
  
  [data-theme='dark'] & {
    background-color: white; /* Keep QR code background white for readability */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 25px;
  gap: 12px;
`;

const Button = styled.button`
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Poppins', sans-serif;

  &.primary {
    background: linear-gradient(to right, var(--primary-color), var(--primary-dark));
    color: white;
    border: none;
    box-shadow: 0 4px 8px rgba(67, 97, 238, 0.2);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 12px rgba(67, 97, 238, 0.3);
    }
  }

  &.secondary {
    background-color: transparent;
    color: var(--text-primary);
    border: 1px solid #e1e1e1;

    &:hover {
      background-color: #f8f9fa;
    }
  }

  &.danger {
    background: linear-gradient(to right, var(--error-color), var(--error-color));
    color: white;
    border: none;
    box-shadow: 0 4px 8px rgba(239, 71, 111, 0.2);

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 6px 12px rgba(239, 71, 111, 0.3);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;

const PageButton = styled.button<{ $active?: boolean }>`
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--paper-color)'};
  color: ${props => props.$active ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-color)' : '#e1e1e1'};
  border-radius: 8px;
  padding: 8px 14px;
  margin: 0 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  font-family: 'Poppins', sans-serif;
  box-shadow: ${props => props.$active ? '0 4px 8px rgba(67, 97, 238, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'};

  &:hover {
    background-color: ${props => props.$active ? 'var(--primary-dark)' : '#f8f9fa'};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  [data-theme='dark'] & {
    border-color: ${props => props.$active ? 'var(--primary-color)' : '#444'};
    box-shadow: ${props => props.$active ? '0 4px 8px rgba(67, 97, 238, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.2)'};
    
    &:hover {
      background-color: ${props => props.$active ? 'var(--primary-dark)' : '#2d2d2d'};
    }
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  margin-bottom: 20px;
  padding: 12px 16px;
  background-color: rgba(239, 71, 111, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
`;

const NoVisitors = styled.div`
  text-align: center;
  padding: 50px 30px;
  background-color: var(--paper-color);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  box-shadow: var(--box-shadow);
  font-size: 1.1rem;
  
  svg {
    width: 80px;
    height: 80px;
    margin-bottom: 20px;
    color: var(--text-light);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(67, 97, 238, 0.2);
  border-radius: 50%;
  border-top-color: var(--primary-color);
  animation: spin 1s ease-in-out infinite;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// Icon components
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.5 14H14.71L14.43 13.73C15.41 12.59 16 11.11 16 9.5C16 5.91 13.09 3 9.5 3C5.91 3 3 5.91 3 9.5C3 13.09 5.91 16 9.5 16C11.11 16 12.59 15.41 13.73 14.43L14 14.71V15.5L19 20.49L20.49 19L15.5 14ZM9.5 14C7.01 14 5 11.99 5 9.5C5 7.01 7.01 5 9.5 5C11.99 5 14 7.01 14 9.5C14 11.99 11.99 14 9.5 14Z" fill="currentColor"/>
  </svg>
);

const ViewQrIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 6.5V9.5H6.5V6.5H9.5ZM11 5H5V11H11V5Z" fill="currentColor"/>
    <path d="M9.5 14.5V17.5H6.5V14.5H9.5ZM11 13H5V19H11V13Z" fill="currentColor"/>
    <path d="M17.5 6.5V9.5H14.5V6.5H17.5ZM19 5H13V11H19V5Z" fill="currentColor"/>
    <path d="M13 13H14.5V14.5H13V13Z" fill="currentColor"/>
    <path d="M14.5 14.5H16V16H14.5V14.5Z" fill="currentColor"/>
    <path d="M16 13H17.5V14.5H16V13Z" fill="currentColor"/>
    <path d="M13 16H14.5V17.5H13V16Z" fill="currentColor"/>
    <path d="M14.5 17.5H16V19H14.5V17.5Z" fill="currentColor"/>
    <path d="M16 16H17.5V17.5H16V16Z" fill="currentColor"/>
    <path d="M17.5 14.5H19V16H17.5V14.5Z" fill="currentColor"/>
    <path d="M17.5 17.5H19V19H17.5V17.5Z" fill="currentColor"/>
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 19C6 20.1 6.9 21 8 21H16C17.1 21 18 20.1 18 19V7H6V19ZM8 9H16V19H8V9ZM15.5 4L14.5 3H9.5L8.5 4H5V6H19V4H15.5Z" fill="currentColor"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 9H15V3H9V9H5L12 16L19 9ZM5 18V20H19V18H5Z" fill="currentColor"/>
  </svg>
);

const NoUserIcon = () => (
  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor" fillOpacity="0.3"/>
  </svg>
);

// Upload modal styles
const TabContainer = styled.div`
  display: flex;
  margin-bottom: 25px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  
  [data-theme='dark'] & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 20px;
  background: ${props => props.active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border: none;
  border-radius: 8px 8px 0 0;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary-color)' : 'rgba(67, 97, 238, 0.1)'};
    transform: translateY(0);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const DropZone = styled.div<{ isDragActive: boolean; hasFiles: boolean }>`
  border: 2px dashed ${(props) => (props.isDragActive ? 'var(--primary-color)' : props.hasFiles ? 'var(--primary-light)' : '#ccc')};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  margin-bottom: 25px;
  background-color: ${(props) => (props.isDragActive ? 'rgba(67, 97, 238, 0.05)' : props.hasFiles ? 'rgba(67, 97, 238, 0.02)' : 'transparent')};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: var(--primary-color);
    background-color: rgba(67, 97, 238, 0.05);
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: ${props => props.hasFiles ? '5px' : '0'};
    background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
    transition: height 0.3s ease;
  }
  
  [data-theme='dark'] & {
    border-color: ${(props) => (props.isDragActive ? 'var(--primary-color)' : props.hasFiles ? 'var(--primary-light)' : '#444')};
    background-color: ${(props) => (props.isDragActive ? 'rgba(67, 97, 238, 0.1)' : props.hasFiles ? 'rgba(67, 97, 238, 0.05)' : 'transparent')};
  }
`;

const DropZoneIcon = styled.div<{ $isDragActive?: boolean }>`
  margin-bottom: 15px;
  
  svg {
    width: 60px;
    height: 60px;
    color: ${props => props.$isDragActive ? 'var(--primary-color)' : '#aaa'};
    transition: color 0.3s ease;
  }
`;

const DropZoneText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 16px;
  font-weight: 500;
`;

const DropZoneHint = styled.p`
  margin: 5px 0 0;
  color: var(--text-light);
  font-size: 14px;
`;

const FilesPreview = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 15px;
  margin-top: 25px;
`;

const FilePreview = styled.div`
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  [data-theme='dark'] & {
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.3);
  }
`;

const VideoPreview = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  
  &::before {
    content: '\25B6';
    color: rgba(255, 255, 255, 0.7);
    font-size: 24px;
    position: absolute;
    z-index: 2;
  }
  
  &::after {
    content: 'VIDEO';
    position: absolute;
    bottom: 8px;
    right: 8px;
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 10px;
    padding: 3px 6px;
    border-radius: 4px;
  }
`;

const PreviewImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveFileButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(239, 71, 111, 0.9);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ProgressContainer = styled.div`
  margin-top: 25px;
`;

const ProgressBar = styled.div<{ progress: number }>`
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
  position: relative;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${(props) => props.progress}%;
    background: linear-gradient(to right, var(--primary-color), var(--primary-light));
    border-radius: 4px;
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
`;

const SuccessMessage = styled.div`
  color: var(--success-color);
  margin-top: 20px;
  padding: 12px 16px;
  background-color: rgba(6, 214, 160, 0.1);
  border-radius: 8px;
  font-weight: 500;
  animation: fadeIn 0.3s ease;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const UploadMediaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 15V18H6V15H4V18C4 19.1 4.9 20 6 20H18C19.1 20 20 19.1 20 18V15H18ZM17 11L15.59 9.59L13 12.17V4H11V12.17L8.41 9.59L7 11L12 16L17 11Z" fill="currentColor"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM14 13V17H10V13H7L12 8L17 13H14Z" fill="currentColor"/>
  </svg>
);

const DropIcon = () => (
  <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14C0 17.31 2.69 20 6 20H19C21.76 20 24 17.76 24 15C24 12.36 21.95 10.22 19.35 10.04ZM19 18H6C3.79 18 2 16.21 2 14C2 11.95 3.53 10.24 5.56 10.03L6.63 9.92L7.13 8.97C8.08 7.14 9.94 6 12 6C14.62 6 16.88 7.86 17.39 10.43L17.69 11.93L19.22 12.04C20.78 12.14 22 13.45 22 15C22 16.65 20.65 18 19 18ZM8 13H10.55V16H13.45V13H16L12 9L8 13Z" fill="currentColor"/>
  </svg>
);

const SuccessIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM16.59 7.58L10 14.17L7.41 11.59L6 13L10 17L18 9L16.59 7.58Z" fill="currentColor"/>
  </svg>
);

const ImageTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.5L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
  </svg>
);

const VideoTabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 10.5V7C17 6.45 16.55 6 16 6H4C3.45 6 3 6.45 3 7V17C3 17.55 3.45 18 4 18H16C16.55 18 17 17.55 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="currentColor"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
  </svg>
);

const ExcelIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z" fill="currentColor"/>
    <path d="M10 19L12 15L14 19" fill="currentColor"/>
    <path d="M8 11H16V13H8V11Z" fill="currentColor"/>
    <path d="M8 15H10V19H14V15H16V19" fill="currentColor"/>
  </svg>
);
const ExportButton = styled(Button)`
  font-size: 14px;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface Visitor {
  id: string;
  token: string;
  visitorName: string;
  createdAt: string;
  mediaCount: number;
}

const ManageVisitors: React.FC = () => {
  const [visitors, setVisitors] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [showQrModal, setShowQrModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  
  // Upload feature states
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [mediaType, setMediaType] = useState<MediaType>('image');
  const [isDragActive, setIsDragActive] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState<string>('');
  const { token } = useAuth();
  // URL for QR code
  const baseUrl = window.location.origin;

  useEffect(() => {
    fetchVisitors();
  }, [currentPage]);

  const fetchVisitors = async (search: string = '') => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Fetching visitors, page:', currentPage);
      const response = await api.get('/qrcodes', {
        params: {
          page: currentPage,
          limit: 10,
          search
        }
      });
      
      console.log('Visitors data:', response.data);
      setVisitors(response.data.visitors);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error fetching visitors:', err);
      setError('Failed to load visitors. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchVisitors(searchQuery);
  };

  const handleViewQR = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowQrModal(true);
  };

  const handleUploadMedia = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setFiles([]);
    setMediaType('image');
    setUploadError('');
    setUploadSuccess('');
    setUploadProgress(0);
    setShowUploadModal(true);
  };

  const handleDelete = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedVisitor) return;
    
    try {
      setDeleteInProgress(true);
      console.log('Deleting visitor:', selectedVisitor.id);
      await api.delete(`/qrcodes/${selectedVisitor.id}`);
      
      // Remove from list
      setVisitors(prev => prev.filter(v => v.id !== selectedVisitor.id));
      setShowDeleteModal(false);
    } catch (err) {
      console.error('Error deleting visitor:', err);
      setError('Failed to delete visitor. Please try again.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const downloadQrCode = () => {
    if (!selectedVisitor) return;
    
    const canvas = document.getElementById('qr-code-modal') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-code-${selectedVisitor.visitorName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };
  
  // Upload functionality handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList: FileList) => {
    const supportedFiles = Array.from(fileList).filter(file => {
      if (mediaType === 'image') {
        return file.type.startsWith('image/');
      } else {
        return file.type.startsWith('video/');
      }
    }) as MediaFile[];
    
    setFiles(prevFiles => [...prevFiles, ...supportedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };
  
  const handleUpload = async () => {
    if (!selectedVisitor || files.length === 0) {
      setUploadError(`Please select at least one ${mediaType}.`);
      return;
    }

    setUploadError('');
    setUploadSuccess('');
    setUploading(true);
    setUploadProgress(0);

    try {
      // Separate images and videos for different upload methods
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      const videoFiles = files.filter(file => file.type.startsWith('video/'));
      
      // Process regular image uploads if any
      if (imageFiles.length > 0 && mediaType === 'image') {
        const formData = new FormData();
        formData.append('visitorId', selectedVisitor.id);
        formData.append('mediaType', 'image');
        
        imageFiles.forEach(file => {
          formData.append('media', file);
        });

        await api.post(`/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              setUploadProgress(percentCompleted);
            }
          }
        });
      }
      
      // Process video uploads with automatic chunking for large files
      if (videoFiles.length > 0 && mediaType === 'video') {
        // For multiple videos, use the batch uploader
        await uploadMultipleVideos({
          files: videoFiles,
          visitorId: selectedVisitor.id,
          token: token,
          onTotalProgress: (progress) => {
            setUploadProgress(progress);
          },
          onError: (error) => {
            console.error('Error uploading video:', error);
            // Continue with other files
          }
        });
      }

      console.log('Upload successful');
      setUploadSuccess(`Successfully uploaded ${files.length} ${mediaType}${files.length > 1 ? 's' : ''}.`);
      setFiles([]);
      setUploadProgress(0);
      
      // Refresh the visitor list to update the media count
      fetchVisitors();
    } catch (err) {
      console.error(`Error uploading ${mediaType}s:`, err);
      setUploadError(`Failed to upload ${mediaType}s. Please try again.`);
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const exportToExcel = async () => {
    try {
      setExportLoading(true);
      setError('');
  
      // Fetch all visitors from API
      const response = await api.get('/qrcodes', {
        params: {
          limit: 1000,
          page: 1
        }
      });
  
      const exportVisitors: Visitor[] = response.data.visitors;
  
      if (!exportVisitors || exportVisitors.length === 0) {
        setError('No visitors to export');
        setExportLoading(false);
        return;
      }
  
      const baseUrl = window.location.origin;
  
      // Prepare Excel data
      const excelData = exportVisitors.map(visitor => ({
        'Visitor Name': visitor.visitorName,
        'Gallery Link': `${baseUrl}/gallery/${visitor.token}`,
        'Media Count': visitor.mediaCount,
        'Created Date': new Date(visitor.createdAt).toLocaleDateString()
      }));
  
      const ws = xlsxUtils.json_to_sheet(excelData);
  
      // Optional: Set column widths
      ws['!cols'] = [
        { wch: 20 },
        { wch: 50 },
        { wch: 12 },
        { wch: 15 }
      ];
  
      const wb = xlsxUtils.book_new();
      xlsxUtils.book_append_sheet(wb, ws, 'Visitors');
  
      const date = new Date().toISOString().split('T')[0];
      const filename = `visitors_export_${date}.xlsx`;
  
      writeFile(wb, filename);
  
      console.log('Excel export complete:', exportVisitors.length, 'visitors exported');
    } catch (err) {
      console.error('Error exporting visitors to Excel:', err);
      setError('Failed to export visitors to Excel. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Manage Visitors</Title>
        {/* Export buttons */}
        <HeaderButtons>
          <ExportButton 
            className="primary" 
            onClick={exportToExcel}
          >
            <ExcelIcon />
            Export to Excel
          </ExportButton>
        </HeaderButtons>
      </Header>

      <SearchContainer>
        <SearchInput
          type="text"
          placeholder="Search by name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />
        <SearchButton onClick={handleSearch}>
          <SearchIcon />
        </SearchButton>
      </SearchContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {loading ? (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      ) : visitors.length === 0 ? (
        <NoVisitors>
          <NoUserIcon />
          <div>No visitors found</div>
        </NoVisitors>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Created Date</TableHeaderCell>
              <TableHeaderCell>Images</TableHeaderCell>
              <TableHeaderCell>Actions</TableHeaderCell>
            </TableHeader>
            {visitors.map((visitor) => (
              <TableRow key={visitor.id}>
                <TableCell>
                  <VisitorName>{visitor.visitorName}</VisitorName>
                </TableCell>
                <TableCell>{formatDate(visitor.createdAt)}</TableCell>
                <TableCell>{visitor.mediaCount}</TableCell>
                <TableCell>
                  <ActionButtonsContainer>
                    <ActionButton onClick={() => handleViewQR(visitor)}>
                      <ViewQrIcon />
                      QR Code
                    </ActionButton>
                    <ActionButton onClick={() => handleUploadMedia(visitor)}>
                      <UploadMediaIcon />
                      Upload
                    </ActionButton>
                    <ActionButton className="delete" onClick={() => handleDelete(visitor)}>
                      <DeleteIcon />
                      Delete
                    </ActionButton>
                  </ActionButtonsContainer>
                </TableCell>
              </TableRow>
            ))}
          </Table>

          <Pagination>
            <PageButton
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </PageButton>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PageButton
                key={page}
                $active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </PageButton>
            ))}
            
            <PageButton
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </PageButton>
          </Pagination>
        </>
      )}

      {/* QR Code Modal */}
      {showQrModal && selectedVisitor && (
        <Modal onClick={() => setShowQrModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>QR Code for {selectedVisitor.visitorName}</ModalTitle>
              <CloseButton onClick={() => setShowQrModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <QrCodeContainer>
              <QrCodeWrapper>
                <QRCode
                  id="qr-code-modal"
                  value={`${baseUrl}/gallery/${selectedVisitor.token}`}
                  size={250}
                  level="H"
                  includeMargin
                  renderAs="canvas"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </QrCodeWrapper>
            </QrCodeContainer>
            
            <ModalButtons>
              <Button className="secondary" onClick={() => setShowQrModal(false)}>
                Close
              </Button>
              <Button className="primary" onClick={downloadQrCode}>
                <DownloadIcon />
                Download QR Code
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedVisitor && (
        <Modal onClick={() => !deleteInProgress && setShowDeleteModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Delete Visitor</ModalTitle>
              <CloseButton onClick={() => !deleteInProgress && setShowDeleteModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <p style={{ fontSize: '16px', lineHeight: '1.6' }}>
              Are you sure you want to delete <strong>{selectedVisitor.visitorName}</strong>?
              This will also delete all associated images and cannot be undone.
            </p>
            
            <ModalButtons>
              <Button 
                className="secondary" 
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteInProgress}
              >
                Cancel
              </Button>
              <Button 
                className="danger" 
                onClick={confirmDelete}
                disabled={deleteInProgress}
              >
                {deleteInProgress ? (
                  <>
                    <LoadingSpinner />
                    Deleting...
                  </>
                ) : (
                  <>
                    <DeleteIcon />
                    Delete
                  </>
                )}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}

      {/* Upload Media Modal */}
      {showUploadModal && selectedVisitor && (
        <Modal onClick={() => !uploading && setShowUploadModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader>
              <ModalTitle>Upload Media for {selectedVisitor.visitorName}</ModalTitle>
              <CloseButton onClick={() => !uploading && setShowUploadModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <TabContainer>
              <Tab 
                active={mediaType === 'image'} 
                onClick={() => {
                  if (!uploading) {
                    setMediaType('image');
                    setFiles([]);
                  }
                }}
              >
                <ImageTabIcon />
                Images
              </Tab>
              <Tab 
                active={mediaType === 'video'} 
                onClick={() => {
                  if (!uploading) {
                    setMediaType('video');
                    setFiles([]);
                  }
                }}
              >
                <VideoTabIcon />
                Videos
              </Tab>
            </TabContainer>

            <DropZone
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              isDragActive={isDragActive}
              hasFiles={files.length > 0}
              onClick={() => document.getElementById('file-input-modal')?.click()}
            >
              <input
                id="file-input-modal"
                type="file"
                multiple
                accept={mediaType === 'image' ? 'image/*' : 'video/*'}
                onChange={handleFileInput}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <DropZoneIcon $isDragActive={isDragActive}>
                <DropIcon />
              </DropZoneIcon>
              <DropZoneText>
                {isDragActive
                  ? `Drop ${mediaType}s here`
                  : `Click or drag & drop ${mediaType}s here to upload`}
              </DropZoneText>
              <DropZoneHint>
                {mediaType === 'image'
                  ? 'Supports: JPEG, PNG, GIF (up to 25MB each)'
                  : 'Supports: MP4, WebM, MOV (up to 100MB each)'}
              </DropZoneHint>

              {files.length > 0 && (
                <FilesPreview>
                  {files.map((file, index) => (
                    <FilePreview key={index}>
                      {file.type.startsWith('image/') ? (
                        <PreviewImage src={URL.createObjectURL(file)} alt={`Preview ${index}`} />
                      ) : (
                        <VideoPreview />
                      )}
                      <RemoveFileButton 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                        disabled={uploading}
                      >
                        <CloseIcon />
                      </RemoveFileButton>
                    </FilePreview>
                  ))}
                </FilesPreview>
              )}
            </DropZone>

            {uploading && (
              <ProgressContainer>
                <ProgressBar progress={uploadProgress} />
                <ProgressText>{uploadProgress}% Uploaded</ProgressText>
              </ProgressContainer>
            )}

            {uploadError && <ErrorMessage>{uploadError}</ErrorMessage>}
            
            {uploadSuccess && (
              <SuccessMessage>
                <SuccessIcon />
                {uploadSuccess}
              </SuccessMessage>
            )}
            
            <ModalButtons>
              <Button 
                className="secondary" 
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
              >
                Close
              </Button>
              <Button 
                className="primary" 
                onClick={handleUpload} 
                disabled={uploading || files.length === 0}
              >
                {uploading ? (
                  <>
                    <LoadingSpinner />
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadIcon />
                    {`Upload ${files.length} ${mediaType === 'image' ? 'Image' : 'Video'}${files.length !== 1 ? 's' : ''}`}
                  </>
                )}
              </Button>
            </ModalButtons>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default ManageVisitors;
